// src/pages/Settings.js
import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";

// Default shape for settings
const DEFAULT_SETTINGS = {
  firstName:   { enabled: false, value: "" },
  lastName:    { enabled: false, value: "" },
  companyName: { enabled: false, value: "" },
  websiteName: { enabled: false, value: "" },
  additional1: { enabled: false, value: "" },
  additional2: { enabled: false, value: "" },
};

function Settings() {
  // Retrieve user ID from localStorage
  const authData = localStorage.getItem("authData");
  const parsedAuthData = authData ? JSON.parse(authData) : null;
  const userId = parsedAuthData?.user?.sub;

  // State
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [credits, setCredits] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const navigate = useNavigate();

  // Helper getters to avoid undefined
  const getEnabled = (key) => settings[key]?.enabled ?? false;
  const getValue   = (key) => settings[key]?.value   ?? "";

  useEffect(() => {
    if (!userId) {
      setError("You are not logged in. Please log in to access settings.");
      navigate("/callback", { replace: true });
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const API_URL = `${process.env.REACT_APP_SETTINGS_URL}?userId=${userId}`;
        const response = await fetch(API_URL, {
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch settings (status ${response.status})`);
        }

        const data = await response.json();

        // Safely parse nested body
        let parsedBody;
        try {
          parsedBody = JSON.parse(data.body);
        } catch (parseErr) {
          throw new Error("Invalid response format from server");
        }

        // Merge defaults with server payload
        const serverSettings = parsedBody.settings || {};
        setSettings({
          ...DEFAULT_SETTINGS,
          ...serverSettings,
        });
        setCredits(parsedBody.creditsRemaining ?? 0);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userId, navigate]);

  const handleCheckboxChange = (field) => {
    setSettings(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        enabled: !getEnabled(field),
      },
    }));
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
      },
    }));
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const UPDATE_URL = process.env.REACT_APP_SETTINGS_URL;
      const res = await fetch(UPDATE_URL, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, settings }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Save failed: ${res.status} ${text}`);
      }
      setSuccess("Settings updated successfully!");
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!userId) {
    return (
      <Layout>
        <div className="container mx-auto mt-10 max-w-4xl bg-white shadow-md p-10 rounded-md">
          <p className="text-red-500">{error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto mt-10 max-w-4xl bg-white shadow-md p-10 rounded-md">
        <h1 className="text-xl font-semibold mb-6">Settings</h1>
        <p className="text-gray-600 mb-6">Manage your user-defined settings and view your remaining credits.</p>

        {/* Credits Display */}
        <div className="mb-6">
          <span className="text-gray-700 font-medium">Remaining Credits:</span>
          {credits === null ? (
            <span className="ml-2 text-blue-500 font-semibold">Loading...</span>
          ) : (
            <span className="ml-2 text-green-500 font-semibold">{credits}</span>
          )}
        </div>

        <form onSubmit={handleSaveSettings}>
          {/* First Name */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={getEnabled("firstName")}
                onChange={() => handleCheckboxChange("firstName")}
                disabled={isLoading}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Your First Name</span>
            </label>
            {getEnabled("firstName") && (
              <input
                type="text"
                value={getValue("firstName")}
                onChange={e => handleInputChange("firstName", e.target.value)}
                placeholder="Enter your first name"
                required
                disabled={isLoading}
                className="mt-2 w-full p-2 border rounded-md"
              />
            )}
          </div>

          {/* Last Name */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={getEnabled("lastName")}
                onChange={() => handleCheckboxChange("lastName")}
                disabled={isLoading}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Last Name</span>
            </label>
            {getEnabled("lastName") && (
              <input
                type="text"
                value={getValue("lastName")}
                onChange={e => handleInputChange("lastName", e.target.value)}
                placeholder="Enter your last name"
                required
                disabled={isLoading}
                className="mt-2 w-full p-2 border rounded-md"
              />
            )}
          </div>

          {/* Company Name */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={getEnabled("companyName")}
                onChange={() => handleCheckboxChange("companyName")}
                disabled={isLoading}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Your Company Name</span>
            </label>
            {getEnabled("companyName") && (
              <input
                type="text"
                value={getValue("companyName")}
                onChange={e => handleInputChange("companyName", e.target.value)}
                placeholder="Enter your company name"
                required
                disabled={isLoading}
                className="mt-2 w-full p-2 border rounded-md"
              />
            )}
          </div>

          {/* Website Name */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={getEnabled("websiteName")}
                onChange={() => handleCheckboxChange("websiteName")}
                disabled={isLoading}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Website Name</span>
            </label>
            {getEnabled("websiteName") && (
              <input
                type="text"
                value={getValue("websiteName")}
                onChange={e => handleInputChange("websiteName", e.target.value)}
                placeholder="Enter your website name"
                required
                disabled={isLoading}
                className="mt-2 w-full p-2 border rounded-md"
              />
            )}
          </div>

          {/* Email (Additional1) */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={getEnabled("additional1")}
                onChange={() => handleCheckboxChange("additional1")}
                disabled={isLoading}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Email</span>
            </label>
            {getEnabled("additional1") && (
              <input
                type="text"
                value={getValue("additional1")}
                onChange={e => handleInputChange("additional1", e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={isLoading}
                className="mt-2 w-full p-2 border rounded-md"
              />
            )}
          </div>

          {/* Additional2 */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={getEnabled("additional2")}
                onChange={() => handleCheckboxChange("additional2")}
                disabled={isLoading}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="ml-2 text-gray-700">Additional 2</span>
            </label>
            {getEnabled("additional2") && (
              <input
                type="text"
                value={getValue("additional2")}
                onChange={e => handleInputChange("additional2", e.target.value)}
                placeholder="Enter additional information"
                required
                disabled={isLoading}
                className="mt-2 w-full p-2 border rounded-md"
              />
            )}
          </div>

          {/* Success & Error */}
          {success && <p className="text-green-500 mb-4">{success}</p>}
          {error   && <p className="text-red-500 mb-4">{error}</p>}

          {/* Actions */}
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => navigate("/generateDraft")}
              disabled={isLoading}
              className="bg-gray-200 text-gray-700 py-2 px-6 rounded-md"
            >
              New Draft
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-blue-500 text-white py-2 px-6 rounded-md"
            >
              {isLoading ? "Saving…" : "Save Settings"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default Settings;
